import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Upload, Users, AlertTriangle, TrendingUp, Star,
  FileSpreadsheet, FileText, Image, Download, Sparkles, Loader2,
  ChevronDown, ChevronUp, Trash2, HelpCircle, Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LearnerProfiles() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  
  const [uploadData, setUploadData] = useState({
    grade: '',
    subject: ''
  });
  
  const [questionnaire, setQuestionnaire] = useState({
    main_challenges: '',
    available_resources: '',
    time_constraints: '',
    previous_interventions: '',
    specific_concerns: ''
  });

  useEffect(() => {
    fetchProfiles();
    fetchSubscription();
  }, []);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/learners/profiles`);
      setProfiles(res.data);
    } catch (error) {
      console.error('Failed to fetch profiles');
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/subscription/status`);
      setSubscription(res.data);
    } catch (error) {
      console.error('Failed to fetch subscription');
    }
  };

  const isPaidSubscriber = subscription?.status === 'active' && 
    ['r25_monthly', 'r50_monthly'].includes(subscription?.subscription_tier);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!uploadData.grade) {
      toast.error('Please select a grade first');
      return;
    }
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API_URL}/api/learners/import?grade=${uploadData.grade}&subject=${uploadData.subject}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Imported ${res.data.total_imported} learners`);
      setShowUpload(false);
      fetchProfiles();
      // Select the new profile
      const profileRes = await axios.get(`${API_URL}/api/learners/profiles/${res.data.profile_id}`);
      setSelectedProfile(profileRes.data);
      setShowQuestionnaire(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProfile = async (profile) => {
    setSelectedProfile(profile);
    if (profile.teacher_responses) {
      setQuestionnaire(profile.teacher_responses);
    } else {
      setQuestionnaire({
        main_challenges: '',
        available_resources: '',
        time_constraints: '',
        previous_interventions: '',
        specific_concerns: ''
      });
    }
  };

  const handleSubmitQuestionnaire = async () => {
    if (!selectedProfile) return;
    
    try {
      await axios.post(
        `${API_URL}/api/learners/profiles/${selectedProfile.profile_id}/questionnaire`,
        questionnaire
      );
      toast.success('Responses saved');
      setShowQuestionnaire(false);
    } catch (error) {
      toast.error('Failed to save responses');
    }
  };

  const handleGenerateRecommendations = async () => {
    if (!selectedProfile) return;
    
    // Save questionnaire first if not empty
    if (questionnaire.main_challenges || questionnaire.specific_concerns) {
      await axios.post(
        `${API_URL}/api/learners/profiles/${selectedProfile.profile_id}/questionnaire`,
        questionnaire
      );
    }
    
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/learners/profiles/${selectedProfile.profile_id}/generate-recommendations`
      );
      setSelectedProfile(prev => ({
        ...prev,
        recommendations: res.data.recommendations
      }));
      toast.success('Recommendations generated');
      fetchProfiles();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('AI recommendations require a paid subscription. Please subscribe to unlock this feature.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate recommendations');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedProfile) return;
    
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/learners/profiles/${selectedProfile.profile_id}/download`
      );
      
      // Convert base64 to blob
      const byteCharacters = atob(res.data.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: res.data.content_type });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/learners/profiles/${profileId}`);
      toast.success('Profile deleted');
      if (selectedProfile?.profile_id === profileId) {
        setSelectedProfile(null);
      }
      fetchProfiles();
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const groupLearners = (learners) => {
    if (!learners) return { at_risk: [], average: [], excellent: [] };
    return {
      at_risk: learners.filter(l => l.category === 'at_risk').sort((a, b) => a.mark - b.mark),
      average: learners.filter(l => l.category === 'average').sort((a, b) => a.mark - b.mark),
      excellent: learners.filter(l => l.category === 'excellent').sort((a, b) => b.mark - a.mark)
    };
  };

  return (
    <div className="min-h-screen bg-sky-50" data-testid="learner-profiles-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <Button
            onClick={() => setShowUpload(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
            data-testid="import-button"
          >
            <Upload className="w-4 h-4 mr-2" /> Import Learners
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
            <Users className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-900">Learner Profiles</h2>
            <p className="text-slate-600">Import marks, profile learners, get AI recommendations</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Profile List */}
          <div className="lg:col-span-1 space-y-4">
            <div className="glass-card p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Your Profiles</h3>
              
              {profiles.length === 0 ? (
                <div className="text-center py-8">
                  <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No profiles yet</p>
                  <p className="text-sm text-slate-400">Import learner data to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {profiles.map(profile => (
                    <div
                      key={profile.profile_id}
                      onClick={() => handleSelectProfile(profile)}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        selectedProfile?.profile_id === profile.profile_id
                          ? 'bg-teal-100 border-2 border-teal-500'
                          : 'bg-white/50 border border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{profile.grade} - {profile.subject || 'General'}</p>
                          <p className="text-sm text-slate-600">{profile.total_learners} learners</p>
                        </div>
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleDeleteProfile(profile.profile_id); }}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                          {profile.summary?.at_risk_count || 0} at risk
                        </span>
                        <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                          {profile.summary?.average_count || 0} avg
                        </span>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                          {profile.summary?.excellent_count || 0} excel
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Profile Details */}
          <div className="lg:col-span-2">
            {!selectedProfile ? (
              <div className="glass-card p-12 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Select a Profile</h3>
                <p className="text-slate-500">Choose a profile from the list or import new learner data</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-slate-900">
                      {selectedProfile.grade} - {selectedProfile.subject || 'General'}
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowQuestionnaire(true)}
                        variant="outline"
                        className="rounded-full"
                      >
                        <HelpCircle className="w-4 h-4 mr-2" /> Teacher Input
                      </Button>
                      <Button
                        onClick={handleDownload}
                        disabled={downloading}
                        variant="outline"
                        className="rounded-full"
                      >
                        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Download Report
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-slate-100 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-slate-900">{selectedProfile.total_learners}</p>
                      <p className="text-sm text-slate-600">Total</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-red-600">{selectedProfile.summary?.at_risk_count || 0}</p>
                      <p className="text-sm text-red-600">At Risk</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{selectedProfile.summary?.average_count || 0}</p>
                      <p className="text-sm text-amber-600">Average</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{selectedProfile.summary?.excellent_count || 0}</p>
                      <p className="text-sm text-green-600">Excellent</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <p className="text-slate-600">Class Average: <span className="font-bold text-2xl text-teal-600">{selectedProfile.summary?.class_average || 0}%</span></p>
                  </div>
                </div>

                {/* Learner Groups */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* At Risk */}
                  <LearnerGroup
                    title="At Risk (0-49%)"
                    subtitle="Levels 1-3 • Needs Support"
                    learners={groupLearners(selectedProfile.learners).at_risk}
                    color="red"
                    icon={AlertTriangle}
                  />
                  
                  {/* Average */}
                  <LearnerGroup
                    title="Average (50-79%)"
                    subtitle="Levels 4-5"
                    learners={groupLearners(selectedProfile.learners).average}
                    color="amber"
                    icon={TrendingUp}
                  />
                  
                  {/* Excellent */}
                  <LearnerGroup
                    title="Excellent (80%+)"
                    subtitle="Levels 6-7"
                    learners={groupLearners(selectedProfile.learners).excellent}
                    color="green"
                    icon={Star}
                  />
                </div>

                {/* AI Recommendations */}
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      AI Recommendations
                      {!isPaidSubscriber && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Premium
                        </span>
                      )}
                    </h3>
                    {isPaidSubscriber ? (
                      <Button
                        onClick={handleGenerateRecommendations}
                        disabled={generating}
                        className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" /> Generate Recommendations
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => navigate('/subscription')}
                        className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                      >
                        <Lock className="w-4 h-4 mr-2" /> Subscribe to Unlock
                      </Button>
                    )}
                  </div>
                  
                  {!isPaidSubscriber && !selectedProfile.recommendations && (
                    <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-xl">
                      <Lock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                      <p className="text-amber-800 font-medium">AI Recommendations - Premium Feature</p>
                      <p className="text-sm text-amber-700 mt-1">Subscribe to R25 or R50 plan to get personalized AI recommendations</p>
                      <Button
                        onClick={() => navigate('/subscription')}
                        className="mt-4 bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                      >
                        View Plans
                      </Button>
                    </div>
                  )}
                  
                  {selectedProfile.recommendations ? (
                    <div className="space-y-6">
                      {selectedProfile.recommendations.at_risk && (
                        <RecommendationCard
                          title="For At Risk Learners"
                          content={selectedProfile.recommendations.at_risk}
                          color="red"
                        />
                      )}
                      {selectedProfile.recommendations.average && (
                        <RecommendationCard
                          title="For Average Learners"
                          content={selectedProfile.recommendations.average}
                          color="amber"
                        />
                      )}
                      {selectedProfile.recommendations.excellent && (
                        <RecommendationCard
                          title="For Excellent Learners"
                          content={selectedProfile.recommendations.excellent}
                          color="green"
                        />
                      )}
                      {selectedProfile.recommendations.whole_class && (
                        <RecommendationCard
                          title="Whole Class Strategies"
                          content={selectedProfile.recommendations.whole_class}
                          color="blue"
                        />
                      )}
                    </div>
                  ) : isPaidSubscriber ? (
                    <div className="text-center py-8 bg-purple-50 rounded-xl">
                      <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                      <p className="text-purple-700">Click "Generate Recommendations" to get AI-powered insights</p>
                      <p className="text-sm text-purple-600 mt-1">Fill in the Teacher Input form first for better results</p>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-8 max-w-lg w-full">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Import Learner Data</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Grade *</label>
                <select
                  value={uploadData.grade}
                  onChange={(e) => setUploadData(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white"
                >
                  <option value="">Select Grade</option>
                  {['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7'].map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject (Optional)</label>
                <Input
                  value={uploadData.subject}
                  onChange={(e) => setUploadData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="e.g., Mathematics"
                />
              </div>
              
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,.docx,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex justify-center gap-4 mb-4">
                  <FileSpreadsheet className="w-8 h-8 text-green-600" />
                  <FileText className="w-8 h-8 text-blue-600" />
                  <Image className="w-8 h-8 text-purple-600" />
                </div>
                <p className="text-slate-600 mb-2">Supported: Excel, CSV, Word, PDF</p>
                <p className="text-sm text-slate-500 mb-4">
                  File should have columns: Name, Surname, Mark (or Score/Percentage)
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || !uploadData.grade}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" /> Choose File
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <Button
                onClick={() => setShowUpload(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Modal */}
      {showQuestionnaire && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="glass-card p-8 max-w-2xl w-full my-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Teacher Input</h3>
            <p className="text-slate-600 mb-6">Help the AI understand your class better for personalized recommendations</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What are the main challenges your learners face?
                </label>
                <textarea
                  value={questionnaire.main_challenges}
                  onChange={(e) => setQuestionnaire(prev => ({ ...prev, main_challenges: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white min-h-[80px]"
                  placeholder="e.g., Language barriers, attention span, foundational gaps..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What resources do you have available?
                </label>
                <textarea
                  value={questionnaire.available_resources}
                  onChange={(e) => setQuestionnaire(prev => ({ ...prev, available_resources: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white min-h-[80px]"
                  placeholder="e.g., Textbooks, worksheets, computers, teaching aids..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What are your time constraints?
                </label>
                <textarea
                  value={questionnaire.time_constraints}
                  onChange={(e) => setQuestionnaire(prev => ({ ...prev, time_constraints: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white min-h-[80px]"
                  placeholder="e.g., 45-minute periods, curriculum deadlines..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What interventions have you tried before?
                </label>
                <textarea
                  value={questionnaire.previous_interventions}
                  onChange={(e) => setQuestionnaire(prev => ({ ...prev, previous_interventions: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white min-h-[80px]"
                  placeholder="e.g., Extra classes, peer tutoring, parent meetings..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Any specific concerns about particular learners?
                </label>
                <textarea
                  value={questionnaire.specific_concerns}
                  onChange={(e) => setQuestionnaire(prev => ({ ...prev, specific_concerns: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white min-h-[80px]"
                  placeholder="e.g., Learners with special needs, behavioral issues..."
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <Button
                onClick={() => setShowQuestionnaire(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitQuestionnaire}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              >
                Save Responses
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Learner Group Component
function LearnerGroup({ title, subtitle, learners, color, icon: Icon }) {
  const [expanded, setExpanded] = useState(true);
  
  const colorClasses = {
    red: 'bg-red-50 border-red-200',
    amber: 'bg-amber-50 border-amber-200',
    green: 'bg-green-50 border-green-200'
  };
  
  const iconColors = {
    red: 'text-red-600',
    amber: 'text-amber-600',
    green: 'text-green-600'
  };
  
  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-3"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColors[color]}`} />
          <div className="text-left">
            <p className="font-semibold text-slate-900">{title}</p>
            <p className="text-xs text-slate-600">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg">{learners.length}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      
      {expanded && (
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {learners.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-2">No learners</p>
          ) : (
            learners.map(learner => (
              <div key={learner.learner_id} className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2">
                <span className="text-sm text-slate-800">
                  {learner.name} {learner.surname}
                </span>
                <span className={`text-sm font-medium ${iconColors[color]}`}>
                  {learner.mark}%
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Recommendation Card Component
function RecommendationCard({ title, content, color }) {
  const colorClasses = {
    red: 'border-l-red-500 bg-red-50',
    amber: 'border-l-amber-500 bg-amber-50',
    green: 'border-l-green-500 bg-green-50',
    blue: 'border-l-blue-500 bg-blue-50'
  };
  
  return (
    <div className={`border-l-4 rounded-r-xl p-4 ${colorClasses[color]}`}>
      <h4 className="font-semibold text-slate-900 mb-2">{title}</h4>
      <p className="text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">{content}</p>
    </div>
  );
}
